import React, { useState, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import _ from "lodash";
import axios from "axios";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import CardImage from "../components/featurematch/CardImage";
import Wrapper from "../components/layout/Wrapper";
import CopyToClipboardButton from "../components/widgets/CopyToClipboardButton";

import PlayerDataEditor from "../components/headtohead/PlayerDataEditor";
import StandingsSelect from "../components/headtohead/StandingsSelect";

import DashboardTable from "../components/featurematch/DashboardTable";
import NotificationWidget from "../components/widgets/NotificationWidget";
import Downloader from "../components/widgets/Downloader";

import HeadToHeadPreview from "./viewers/HeadToHeadPreview";

//HOOKS
import useHeadToHead from "../hooks/useHeadToHead";
import useDatabaseManagement from "../hooks/useDatabaseManagement";

export default function HeadToHeadEditor({ parent = "" }) {
   let { id } = useParams();

   const columnWidth = useRef();

   const iframeScale =
      columnWidth.current &&
      (parseInt(window.getComputedStyle(columnWidth.current).width) -
         parseInt(
            window
               .getComputedStyle(columnWidth.current)
               .getPropertyValue("padding-left")
         )) /
         1920;

   // WINDOW LISTENER
   const [windowSize, setWindowSize] = useState({});

   useEffect(() => {
      function handleResize() {
         setWindowSize({
            height: window.innerHeight,
            width: window.innerWidth,
         });
      }

      window.addEventListener("resize", handleResize);

      return (_) => {
         window.removeEventListener("resize", handleResize);
      };
   });

   useEffect(() => {
      setWindowSize({
         height: window.innerHeight,
         width: window.innerWidth,
      });
   }, []);

   const [
      notification,
      allHeadToHead,
      headToHead,
      show,
      runTimestamp,
      { update, getAllHeadToHead, getHeadToHead, headToHeadViewerLoad },
   ] = useHeadToHead();

   const [dbLog, previewData, dbNotification, { getPlayerData }] =
      useDatabaseManagement();

   useEffect(() => {
      getHeadToHead(id);
   }, []);

   useEffect(() => {
      // console.log(headToHead);
   }, [headToHead]);

   const handleGetPlayerData = (e) => {
      getPlayerData(e.target.id).then((response) => {
         getHeadToHead(id);
      });
   };

   const handlePlayerChange = (updates) => {
      let newDataFields = headToHead.DataFields;
      let clearDecks = {
         DataFields: {
            ...newDataFields,
            PlayerDecklistP1: "",
            PlayerDecklistP2: "",
         },
      };
      update(id, { ...updates, ...clearDecks });
   };

   const handleLoadSlide = (e) => {
      headToHeadViewerLoad(e.target.id);
   };

   const handleChangeStandings = (e) => {
      const newStandingsId = e.id !== 0 ? e.id : null;
      update(id, { StandingsId: e.id }, newStandingsId);
   };

   const handleWinStreakToggle = (checked) => {
      if (headToHead) {
         const newDataFields = {
            ...(headToHead.DataFields || {}),
            showWinStreak: checked
         };
         const standingsId = headToHead.StandingsId || null;
         update(id, { DataFields: newDataFields }, standingsId);
      }
   };

   const handleShowRecordToggle = (checked) => {
      if (headToHead) {
         const newDataFields = {
            ...(headToHead.DataFields || {}),
            showRecord: checked
         };
         const standingsId = headToHead.StandingsId || null;
         update(id, { DataFields: newDataFields }, standingsId);
      }
   };

   const handleNumRoundsChange = (numRounds) => {
      if (headToHead) {
         const newDataFields = {
            ...(headToHead.DataFields || {}),
            numRounds: numRounds
         };
         const standingsId = headToHead.StandingsId || null;
         update(id, { DataFields: newDataFields }, standingsId);
      }
   };

   const handleDataFieldUpdate = (e) => {
      let newDataFields = headToHead.DataFields;

      if (isNaN(e.target.id)) {
         update(id, {
            DataFields: {
               ...newDataFields,
               [e.target.id]: e.target.value,
            },
         });
      } else {
         newDataFields.data[e.target.id] = {
            title: e.target.value,
            field: e.target.value,
         };
         update(id, { DataFields: newDataFields });
      }
   };

   return (
      <Wrapper>
         <Stack direction='row' alignItems='center' sx={{ mb: 2 }}>
            <Typography
               variant='h3'
               component='div'
               sx={{ textTransform: "uppercase", width: "100%" }}
            >
               Head To Head
            </Typography>
      
            {headToHead && headToHead.Player1Data && headToHead.Player2Data && (
               <Downloader
                  enabled={true}
                  downloadName={
                     "HeadToHead_" +
                     (headToHead.Player1Data &&
                     headToHead.Player1Data.PlayerNameLastFirst
                        ? headToHead.Player1Data.PlayerNameLastFirst.split(
                             ","
                          )[0].replace(/[^A-Za-z0-9]/g, "")
                        : "none") +
                     "_vs_" +
                     (headToHead.Player2Data &&
                     headToHead.Player2Data.PlayerNameLastFirst
                        ? headToHead.Player2Data.PlayerNameLastFirst.split(
                             ","
                          )[0].replace(/[^A-Za-z0-9]/g, "")
                        : "none") +
                     "_" +
                     Date.now()
                  }
               >
                  <HeadToHeadPreview data={headToHead} show={true} />
               </Downloader>
            )}
         </Stack>

         <Grid container spacing={3} sx={{ mb: 8 }}>
            <Grid
               item
               xs={6}
               ref={columnWidth}
               sx={{ height: 1080 * iframeScale, position: "relative" }}
            >
               <Box
                  sx={{
                     position: "relative",
                     width: 1920,
                     height: 1080,
                     transform: "scale(" + iframeScale + ")",
                     transformOrigin: "top left",
                     overflow: "hidden",
                     border: "1px solid #333",
                  }}
                  className='transparentBG'
               >
                  <HeadToHeadPreview data={headToHead} show={true} />
               </Box>
            </Grid>
            {headToHead && (
               <Grid item xs={6}>
                  <Stack
                     sx={{
                        backgroundColor: "#fff",
                        textAlign: "center",
                        p: 4,
                     }}
                  >
                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h5'
                           color='text.dark'
                           component='div'
                           sx={{
                              textAlign: "center",
                              textTransform: "uppercase",
                              mb: 2,
                           }}
                        >
                           Selected Data Fields
                        </Typography>
                        <Stack spacing={1}>
                           {headToHead.DataFields &&
                              headToHead.DataFields.data.map((row, i) => (
                                 <Box key={"field" + i}>
                                    {row.title}
                                    {row.field && (
                                       <>
                                          {parent != "producer" && (
                                             <Button
                                                size='small'
                                                variant='outlined'
                                                color='error'
                                                sx={{ ml: 2 }}
                                                id={i}
                                                value=''
                                                onClick={handleDataFieldUpdate}
                                             >
                                                Clear
                                             </Button>
                                          )}
                                       </>
                                    )}
                                 </Box>
                              ))}
                        </Stack>
                     </Box>

                     <Box sx={{ mb: 4 }}>
                        <Typography
                           variant='h5'
                           color='text.dark'
                           component='div'
                           sx={{
                              textAlign: "center",
                              textTransform: "uppercase",
                              mb: 2,
                           }}
                        >
                           Selected Decklists
                        </Typography>
                        <Stack spacing={1}>
                           {headToHead.Player1Decklist && (
                              <Box>
                                 Player 1: {headToHead.Player1Decklist.Format} |{" "}
                                 {headToHead.Player1Decklist.DecklistName}
                                 {parent != "producer" && (
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='error'
                                       sx={{ ml: 2 }}
                                       id={"PlayerDecklistP1"}
                                       value=''
                                       onClick={handleDataFieldUpdate}
                                    >
                                       Clear
                                    </Button>
                                 )}
                              </Box>
                           )}

                           {headToHead.Player2Decklist && (
                              <Box>
                                 Player 2: {headToHead.Player2Decklist.Format} |{" "}
                                 {headToHead.Player2Decklist.DecklistName}
                                 {parent != "producer" && (
                                    <Button
                                       size='small'
                                       variant='outlined'
                                       color='error'
                                       sx={{ ml: 2 }}
                                       id={"PlayerDecklistP2"}
                                       value=''
                                       onClick={handleDataFieldUpdate}
                                    >
                                       Clear
                                    </Button>
                                 )}
                              </Box>
                           )}
                        </Stack>
                     </Box>

                     <Box>
                        <Typography
                           variant='h5'
                           color='text.dark'
                           component='div'
                           sx={{
                              textAlign: "center",
                              textTransform: "uppercase",
                           }}
                        >
                           Standings Data
                        </Typography>

                        <StandingsSelect
                           selected={
                              headToHead.StandingsId
                                 ? headToHead.StandingsId
                                 : 0
                           }
                           callback={handleChangeStandings}
                        />
                     </Box>

                     <Box sx={{ mb: 4 }}>
                        <Stack spacing={1} alignItems='center'>
                           <FormControlLabel
                              control={
                                 <Switch
                                    checked={headToHead.DataFields?.showRecord || false}
                                    onChange={(e) => handleShowRecordToggle(e.target.checked)}
                                    size="medium"
                                 />
                              }
                              label={headToHead.DataFields?.showRecord ? "Show Record" : "Hide Record"}
                              labelPlacement="end"
                           />
                           <FormControlLabel
                              control={
                                 <Switch
                                    checked={headToHead.DataFields?.showWinStreak || false}
                                    onChange={(e) => handleWinStreakToggle(e.target.checked)}
                                    size="medium"
                                 />
                              }
                              label={headToHead.DataFields?.showWinStreak ? "Show Win Streak" : "Hide Win Streak"}
                              labelPlacement="end"
                           />
                           <Box>
                              <Typography variant='body2' sx={{ mb: 1, textAlign: "center" }}>
                                 Number of Rounds
                              </Typography>
                              <Select
                                 value={headToHead.DataFields?.numRounds || 5}
                                 onChange={(e) => handleNumRoundsChange(Number(e.target.value))}
                                 size='small'
                                 sx={{ minWidth: 120 }}
                              >
                                 <MenuItem value={3}>3</MenuItem>
                                 <MenuItem value={4}>4</MenuItem>
                                 <MenuItem value={5}>5</MenuItem>
                                 <MenuItem value={6}>6</MenuItem>
                              </Select>
                           </Box>
                        </Stack>
                     </Box>
                  </Stack>
               </Grid>
            )}
         </Grid>

         {headToHead && parent != "producer" && (
            <Stack>
               <Grid container spacing={4}>
                  <Grid item xs={6} sx={{ textAlign: "center" }}>
                     <PlayerDataEditor
                        playerPosition={1}
                        playerId={headToHead.PlayerIdP1}
                        activeDataFields={headToHead.DataFields}
                        data={headToHead.Player1Data}
                        handleDataFieldUpdate={handleDataFieldUpdate}
                        handlePlayerChange={handlePlayerChange}
                        handleGetPlayerData={handleGetPlayerData}
                     />
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "center" }}>
                     <PlayerDataEditor
                        playerPosition={2}
                        playerId={headToHead.PlayerIdP2}
                        activeDataFields={headToHead.DataFields}
                        data={headToHead.Player2Data}
                        handleDataFieldUpdate={handleDataFieldUpdate}
                        handlePlayerChange={handlePlayerChange}
                        handleGetPlayerData={handleGetPlayerData}
                     />
                  </Grid>
               </Grid>
            </Stack>
         )}

         {notification && (
            <NotificationWidget
               notification={notification.message}
               key={"notification" + notification.date}
            />
         )}
      </Wrapper>
   );
}
